'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { usePathname, useRouter } from 'next/navigation'
import Image from 'next/image'

import {
  formModalClicked,
  formModalClosed,
  formModalShown,
  formModalSubmitted,
} from 'clients/segment'
import { useAnonymousId } from 'components/app/anonymousIdContext'
import { nextImageHelper } from 'utils/next-image-css-helper'
import cn from 'utils/classnames'
import { Modal } from '../modal'
import { CreditDisclosure } from '../credit-disclosure'
import { type Option, OptionButton } from './OptionButton'
import { Loader } from './Loader'

export interface Question {
  _id: string
  question: string
  description?: string
  creditDisclosure?: boolean
  options: Option[]
}

interface QuestionFormProps {
  questions: Question[]
}

interface Answers {
  [questionId: string]: Option['id']
}

export const QuestionForm = ({ questions }: QuestionFormProps) => {
  const router = useRouter()
  const pathname = usePathname()
  const [isOpen, setIsOpen] = useState(true)
  const [answers, setAnswers] = useState<Answers>({})
  const [currentQuestionId, setCurrentQuestionId] = useState<string>(
    questions[0]?._id,
  )
  const [isLoading, setIsLoading] = useState(false)
  const hasShownRef = useRef(false)

  const anonymousId = useAnonymousId()
  const userContext = useMemo(() => ({ anonymousId }), [anonymousId])

  const currentQuestion = useMemo(
    () =>
      questions.find((question) => question._id === currentQuestionId) ?? null,
    [questions, currentQuestionId],
  )

  const handleClose = useCallback(() => {
    formModalClosed({
      page_path: pathname,
      user_context: userContext,
    })
    setIsOpen(false)
  }, [pathname, userContext])

  useEffect(() => {
    if (isOpen && !hasShownRef.current) {
      formModalShown({
        page_path: pathname,
        user_context: userContext,
      })
      hasShownRef.current = true
    }
  }, [isOpen, pathname, userContext])

  const handleSelect = useCallback(
    (option: Option) => {
      if (!currentQuestion) return

      formModalClicked({
        page_path: pathname,
        user_context: userContext,
        option: option.option,
      })

      const nextAnswers = {
        ...answers,
        [currentQuestion._id]: option.id,
      }
      setAnswers(nextAnswers)

      if (option.nextQuestion) {
        setCurrentQuestionId(option.nextQuestion)
        return
      }

      const eventAnswers: Record<string, string> = {}
      Object.entries(nextAnswers).forEach(([questionId, optionId]) => {
        const question = questions.find(
          (question) => question._id === questionId,
        )
        const option = question?.options.find(
          (option) => option.id === optionId,
        )
        if (question && option) {
          eventAnswers[question.question] = option.option
        }
      })

      formModalSubmitted({
        page_path: pathname,
        user_context: userContext,
        answers: eventAnswers,
      })

      if (option.url) {
        const optionUrl = new URL(option.url)

        if (pathname === optionUrl.pathname) {
          setIsOpen(false)
          return
        }

        setIsLoading(true)

        const separator = option.url.includes('?') ? '&' : '?'
        const origin = encodeURIComponent(pathname)
        router.push(
          `${option.url}${separator}fromModalRedirect=true&originPath=${origin}`,
        )

        return
      }

      setIsOpen(false)
    },
    [answers, currentQuestion, pathname, router, userContext, questions],
  )

  if (!currentQuestion) return null

  return (
    <Modal onClose={handleClose} open={isOpen}>
      <div className="relative max-w-xl mx-auto px-4 sm:px-8 py-8 rounded bg-white">
        <button
          type="button"
          aria-label="Close form"
          onClick={handleClose}
          className="absolute top-2 right-2 opacity-40"
        >
          <Image
            src="/static/icons/icon-close-circle-black.svg"
            alt="Close icon"
            height={32}
            width={32}
            style={nextImageHelper.intrinsic}
          />
        </button>
        {isLoading ? (
          <Loader />
        ) : (
          <>
            <div className="flex flex-col pt-2 sm:pt-0 text-center">
              {currentQuestion.description && (
                <>
                  <p className="px-10 sm:text-xl font-semibold text-primary leading-tight">
                    {currentQuestion.description}
                  </p>
                  <hr className="w-28 h-1 mx-auto my-4 bg-tetriary-dark" />
                </>
              )}
              <h2
                className={cn(
                  'px-12 sm:px-0 text-2xl sm:text-3xl font-serif font-bold text-primary leading-tight',
                  !currentQuestion.creditDisclosure && 'px-0 sm:px-14',
                )}
              >
                {currentQuestion.question}
                {currentQuestion.creditDisclosure && (
                  <CreditDisclosure iconColor="gray" inlineBlock />
                )}
              </h2>
            </div>
            {!currentQuestion.description && (
              <hr className="w-28 h-1 mx-auto my-4 bg-tetriary-dark" />
            )}
            <div className="flex flex-col gap-3 sm:px-14 pt-2">
              {currentQuestion.options.map((option) => (
                <OptionButton
                  key={option.id}
                  option={option}
                  selected={answers[currentQuestion._id] === option.id}
                  onSelect={handleSelect}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}
